import React, { useState, useEffect } from 'react';
import { Save, Smartphone, Monitor, Tablet, LayoutGrid } from 'lucide-react';
import { discoverService, DiscoverSettings } from '../../services/DiscoverService';
import { useToast } from '../../context/ToastContext';
import { supabase } from '../../lib/supabase';
import { compressImage } from '../../utils/imageOptimizer';
import { FramingSettings } from '../../types';

const DEFAULT_FRAMING: FramingSettings = {
    mobile: { scale: 1, x: 50, y: 50 },
    tablet: { scale: 1, x: 50, y: 50 },
    desktop: { scale: 1, x: 50, y: 50 }
};

const DEFAULT_SETTINGS: DiscoverSettings = {
    id: '00000000-0000-0000-0000-000000000000',
    hero_badge: 'Ingeniería en cada detalle',
    hero_title: 'Sincera Perfección.',
    hero_description: 'No inventamos materiales, los perfeccionamos. Carbon Case SV nace de la necesidad de una protección que no comprometa el diseño original.',
    classic_image_url: 'https://images.unsplash.com/photo-1550537687-c91072c4792d?q=80&w=1000&auto=format&fit=crop',
    classic_image_mobile_url: '',
    classic_framing_settings: { ...DEFAULT_FRAMING },
    classic_badge: 'El Estándar',
    classic_title: 'Fibra Clásica',
    classic_description: 'El tejido 3K Twill tradicional. Simetría perfecta, peso pluma y una estética que nunca pasa de moda.',
    forged_image_url: 'https://images.unsplash.com/photo-1542393545-10f5cde2c810?q=80&w=1000&auto=format&fit=crop',
    forged_image_mobile_url: '',
    forged_framing_settings: { ...DEFAULT_FRAMING },
    forged_badge: 'La Vanguardia',
    forged_title: 'Fibra Forjada',
    forged_description: 'Cada patrón es irrepetible. Fragmentos de carbono prensados a alta presión para un look tipo mármol industrial.',
    magsafe_title: 'MagSafe Architecture.',
    magsafe_description: 'Nuestros imanes están integrados directamente en la estructura de carbono, garantizando una conexión fuerte sin añadir milímetros al chasis.',
    magsafe_framing_settings: { ...DEFAULT_FRAMING },
    unboxing_badge: 'The Arrival',
    unboxing_title: 'Más que un accesorio, una pieza de colección.',
    unboxing_description: 'Cada Carbon Case llega en un empaque rígido con textura mate, diseñado para proteger la pieza durante el trayecto y ser el inicio de una experiencia premium.',
    unboxing_image_url: 'https://images.unsplash.com/photo-1621330396173-e41b1cafd17f?q=80&w=1000&auto=format&fit=crop',
    unboxing_image_mobile_url: '',
    unboxing_framing_settings: { ...DEFAULT_FRAMING },
    unboxing_feature_1: 'Caja Rígida Premium',
    unboxing_feature_2: '2 Stickers de Edición Especial',
    unboxing_feature_3: 'Guía de Cuidado',
    cta_title: '¿Listo para el Siguiente Nivel?'
};

type NarrativeTab = 'classic' | 'forged' | 'magsafe' | 'unboxing' | 'cta';
type DeviceType = 'mobile' | 'tablet' | 'desktop';

const NarrativeSettings: React.FC = () => {
    const [settings, setSettings] = useState<DiscoverSettings>(DEFAULT_SETTINGS);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<NarrativeTab>('classic');
    const [previewDevice, setPreviewDevice] = useState<DeviceType>('desktop');

    // File upload states (Simplified to 1 per section)
    const [pendingFiles, setPendingFiles] = useState<{
        classic: File | null;
        forged: File | null;
        unboxing: File | null;
    }>({
        classic: null,
        forged: null,
        unboxing: null,
    });

    const { showToast } = useToast();

    useEffect(() => {
        const fetchSettings = async () => {
            const data = await discoverService.getSettings();
            if (data) {
                // Ensure framing settings exist for all sections
                const mergedData = {
                    ...data,
                    classic_framing_settings: data.classic_framing_settings || { ...DEFAULT_FRAMING },
                    forged_framing_settings: data.forged_framing_settings || { ...DEFAULT_FRAMING },
                    magsafe_framing_settings: data.magsafe_framing_settings || { ...DEFAULT_FRAMING },
                    unboxing_framing_settings: data.unboxing_framing_settings || { ...DEFAULT_FRAMING }
                };
                setSettings(mergedData);
            }
            setIsLoading(false);
        };
        fetchSettings();
    }, []);

    const handleChange = (field: keyof DiscoverSettings, value: any) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleFramingChange = (section: NarrativeTab, device: DeviceType, field: 'scale' | 'x' | 'y', value: number) => {
        const framingKey = `${section}_framing_settings` as keyof DiscoverSettings;
        const currentFraming = (settings[framingKey] as FramingSettings) || { ...DEFAULT_FRAMING };

        const updatedFraming = {
            ...currentFraming,
            [device]: {
                ...currentFraming[device],
                [field]: value
            }
        };

        handleChange(framingKey, updatedFraming);
    };

    const uploadImage = async (file: File, prefix: string): Promise<string | null> => {
        try {
            const uploadFile = await compressImage(file, { maxWidth: 1920, quality: 0.8 });
            const fileExt = file.name.split('.').pop();
            const fileName = `${prefix}-${Date.now()}.${fileExt}`;

            const { error } = await supabase.storage
                .from('sections')
                .upload(fileName, uploadFile);

            if (error) throw error;

            const { data } = supabase.storage
                .from('sections')
                .getPublicUrl(fileName);

            return data.publicUrl;
        } catch (e: any) {
            console.error(`Upload error for ${prefix}:`, e);
            return null;
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const newSettings = { ...settings };

        // Upload any pending files
        if (pendingFiles.classic) {
            const url = await uploadImage(pendingFiles.classic, 'classic');
            if (url) newSettings.classic_image_url = url;
        }
        if (pendingFiles.forged) {
            const url = await uploadImage(pendingFiles.forged, 'forged');
            if (url) newSettings.forged_image_url = url;
        }
        if (pendingFiles.unboxing) {
            const url = await uploadImage(pendingFiles.unboxing, 'unboxing');
            if (url) newSettings.unboxing_image_url = url;
        }

        const { id, ...updateData } = newSettings;
        const success = await discoverService.updateSettings(updateData);

        if (success) {
            setSettings(newSettings);
            setPendingFiles({ classic: null, forged: null, unboxing: null });
            showToast('Configuración narrativa actualizada', 'success');
        } else {
            showToast('Error al actualizar la configuración', 'error');
        }
        setIsSaving(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
            </div>
        );
    }

    const inputClasses = "w-full bg-zinc-900 border border-zinc-800 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600 transition-colors";
    const labelClasses = "block text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2";
    const sectionClasses = "bg-zinc-950 border border-zinc-800 rounded-xl p-6 sm:p-8 space-y-6";

    // Reusable Framing Controls Render Function (Avoids component remounts on drag)
    const renderFramingControls = (section: NarrativeTab) => {
        const framingKey = `${section}_framing_settings` as keyof DiscoverSettings;
        const framing = (settings[framingKey] as FramingSettings) || DEFAULT_FRAMING;
        const current = framing[previewDevice];
        const imageUrl = settings[`${section}_image_url` as keyof DiscoverSettings] as string;

        return (
            <div className="space-y-6 bg-black/40 p-4 rounded-lg border border-zinc-800">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Ajuste de Encuadre Responsivo</span>
                    <div className="flex bg-zinc-900 rounded-lg p-1">
                        {(['mobile', 'tablet', 'desktop'] as DeviceType[]).map((device) => (
                            <button
                                key={device}
                                onClick={() => setPreviewDevice(device)}
                                className={`p-1.5 rounded transition-all ${previewDevice === device ? 'bg-white text-black shadow-lg' : 'text-zinc-500 hover:text-white'}`}
                            >
                                {device === 'mobile' && <Smartphone size={14} />}
                                {device === 'tablet' && <Tablet size={14} />}
                                {device === 'desktop' && <Monitor size={14} />}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preview Window */}
                <div
                    className="relative mx-auto bg-zinc-900 border border-zinc-700 overflow-hidden transition-all duration-300"
                    style={{
                        width: previewDevice === 'mobile' ? '140px' : previewDevice === 'tablet' ? '240px' : '220px',
                        aspectRatio: previewDevice === 'mobile' ? '342/600' :
                            previewDevice === 'tablet' ? '720/600' :
                                section === 'unboxing' ? '876/800' : '592/600'
                    }}
                >
                    <img
                        src={imageUrl}
                        className="absolute inset-0 w-full h-full transition-all duration-500"
                        style={{
                            objectFit: 'cover',
                            transform: `scale(${current.scale})`,
                            objectPosition: `${current.x}% ${current.y}%`
                        }}
                    />
                    {/* Safe Zone Grid Overlay */}
                    <div className="absolute inset-0 pointer-events-none grid grid-cols-3 grid-rows-3 opacity-20">
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                        <div className="border border-white/30" />
                    </div>
                    {/* SAFE ZONE FOR TEXT (BOTTOM) */}
                    <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-4 pointer-events-none">
                        <div className="w-full">
                            <div className="h-1.5 w-1/3 bg-white/30 rounded-full mb-2" />
                            <div className="h-3 w-2/3 bg-white/60 rounded" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <div className="flex justify-between text-[10px] font-bold uppercase text-zinc-500">
                            <span>Zoom (Scale)</span>
                            <span className="text-white">{current.scale.toFixed(2)}x</span>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="3"
                            step="0.05"
                            value={current.scale}
                            onChange={(e) => handleFramingChange(section, previewDevice, 'scale', parseFloat(e.target.value))}
                            className="w-full accent-white"
                        />
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between text-[10px] font-bold uppercase text-zinc-500">
                            <span>Posición X</span>
                            <span className="text-white">{current.x}%</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={current.x}
                            onChange={(e) => handleFramingChange(section, previewDevice, 'x', parseInt(e.target.value))}
                            className="w-full accent-white"
                        />
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between text-[10px] font-bold uppercase text-zinc-500">
                            <span>Posición Y</span>
                            <span className="text-white">{current.y}%</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={current.y}
                            onChange={(e) => handleFramingChange(section, previewDevice, 'y', parseInt(e.target.value))}
                            className="w-full accent-white"
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8 animate-fade-in relative pb-10 font-sans">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="bg-zinc-900/50 p-3 rounded-lg border border-zinc-800 flex items-center gap-3">
                    <LayoutGrid className="text-zinc-500 w-5 h-5" />
                    <p className="text-zinc-400 text-sm">Configuración de Experiencia responsiva con encuadre inteligente.</p>
                </div>

                <div className="sticky top-20 z-10 w-full sm:w-auto">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-black px-8 py-3 rounded-lg font-bold hover:bg-zinc-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest shadow-[0_0_20px_rgba(255,255,255,0.2)] active:scale-95"
                    >
                        {isSaving ? (
                            <div className="h-4 w-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Actualizar Experiencia
                    </button>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="flex overflow-x-auto border-b border-zinc-800 scrollbar-hide">
                {[
                    { id: 'classic', label: '1. Clásica' },
                    { id: 'forged', label: '2. Forjada' },
                    { id: 'magsafe', label: '3. MagSafe' },
                    { id: 'unboxing', label: '4. Unboxing' },
                    { id: 'cta', label: '5. CTA Final' }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as NarrativeTab)}
                        className={`px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] whitespace-nowrap transition-all border-b-2 ${activeTab === tab.id
                            ? 'text-white border-white'
                            : 'text-zinc-600 border-transparent hover:text-zinc-400'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="space-y-8">
                {/* Classic Carbon */}
                {activeTab === 'classic' && (
                    <div className={`${sectionClasses} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-6">
                                <h2 className="text-xl font-bold text-white uppercase italic tracking-tighter">Fibra de Carbono Clásica</h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className={labelClasses}>Imagen Maestra (Alta resolución)</label>
                                        <div className="relative group overflow-hidden bg-zinc-900 rounded-lg border border-dashed border-zinc-800 hover:border-zinc-600 transition-all p-4">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setPendingFiles(prev => ({ ...prev, classic: e.target.files?.[0] || null }))}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded bg-black border border-zinc-800 overflow-hidden">
                                                    <img
                                                        src={pendingFiles.classic ? URL.createObjectURL(pendingFiles.classic) : settings.classic_image_url}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-bold text-white uppercase tracking-widest">{pendingFiles.classic ? pendingFiles.classic.name : 'Imagen actual'}</p>
                                                    <p className="text-[10px] text-zinc-500 uppercase mt-1">Arrastra para reemplazar</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className={labelClasses}>Badge</label>
                                        <input
                                            type="text"
                                            value={settings.classic_badge}
                                            onChange={(e) => handleChange('classic_badge', e.target.value)}
                                            className={inputClasses}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Título</label>
                                        <input
                                            type="text"
                                            value={settings.classic_title}
                                            onChange={(e) => handleChange('classic_title', e.target.value)}
                                            className={inputClasses}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Descripción</label>
                                        <textarea
                                            value={settings.classic_description}
                                            onChange={(e) => handleChange('classic_description', e.target.value)}
                                            rows={3}
                                            className={inputClasses}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {renderFramingControls('classic')}
                            </div>
                        </div>
                    </div>
                )}

                {/* Forged Carbon */}
                {activeTab === 'forged' && (
                    <div className={`${sectionClasses} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-6">
                                <h2 className="text-xl font-bold text-white uppercase italic tracking-tighter">Bloque: Fibra Forjada</h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className={labelClasses}>Imagen Maestra</label>
                                        <div className="relative group overflow-hidden bg-zinc-900 rounded-lg border border-dashed border-zinc-800 hover:border-zinc-600 transition-all p-4">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setPendingFiles(prev => ({ ...prev, forged: e.target.files?.[0] || null }))}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded bg-black border border-zinc-800 overflow-hidden">
                                                    <img
                                                        src={pendingFiles.forged ? URL.createObjectURL(pendingFiles.forged) : settings.forged_image_url}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-bold text-white uppercase tracking-widest">{pendingFiles.forged ? pendingFiles.forged.name : 'Imagen actual'}</p>
                                                    <p className="text-[10px] text-zinc-500 uppercase mt-1">Click para reemplazar</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className={labelClasses}>Badge</label>
                                        <input
                                            type="text"
                                            value={settings.forged_badge}
                                            onChange={(e) => handleChange('forged_badge', e.target.value)}
                                            className={inputClasses}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Título</label>
                                        <input
                                            type="text"
                                            value={settings.forged_title}
                                            onChange={(e) => handleChange('forged_title', e.target.value)}
                                            className={inputClasses}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Descripción</label>
                                        <textarea
                                            value={settings.forged_description}
                                            onChange={(e) => handleChange('forged_description', e.target.value)}
                                            rows={3}
                                            className={inputClasses}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-6">
                                {renderFramingControls('forged')}
                            </div>
                        </div>
                    </div>
                )}

                {/* MagSafe Tech */}
                {activeTab === 'magsafe' && (
                    <div className={`${sectionClasses} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-6">
                                <h2 className="text-xl font-bold text-white uppercase italic tracking-tighter">MagSafe Architecture</h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className={labelClasses}>Título Técnico</label>
                                        <input
                                            type="text"
                                            value={settings.magsafe_title}
                                            onChange={(e) => handleChange('magsafe_title', e.target.value)}
                                            className={inputClasses}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Descripción</label>
                                        <textarea
                                            value={settings.magsafe_description}
                                            onChange={(e) => handleChange('magsafe_description', e.target.value)}
                                            rows={4}
                                            className={inputClasses}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* MagSafe no utiliza imagen de fondo actualmente en TechSection, 
                                por lo que ocultamos el control de encuadre para optimizar el UX del admin */}
                        </div>
                    </div>
                )}

                {/* Unboxing Experience */}
                {activeTab === 'unboxing' && (
                    <div className={`${sectionClasses} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-6">
                                <h2 className="text-xl font-bold text-white uppercase italic tracking-tighter">Unboxing Experience</h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className={labelClasses}>Imagen Maestra</label>
                                        <div className="relative group overflow-hidden bg-zinc-900 rounded-lg border border-dashed border-zinc-800 hover:border-zinc-600 transition-all p-4">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setPendingFiles(prev => ({ ...prev, unboxing: e.target.files?.[0] || null }))}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded bg-black border border-zinc-800 overflow-hidden">
                                                    <img
                                                        src={pendingFiles.unboxing ? URL.createObjectURL(pendingFiles.unboxing) : settings.unboxing_image_url}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-bold text-white uppercase tracking-widest">{pendingFiles.unboxing ? pendingFiles.unboxing.name : 'Imagen actual'}</p>
                                                    <p className="text-[10px] text-zinc-500 uppercase mt-1">Hacer click para cambiar</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClasses}>Badge</label>
                                            <input
                                                type="text"
                                                value={settings.unboxing_badge}
                                                onChange={(e) => handleChange('unboxing_badge', e.target.value)}
                                                className={inputClasses}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>Título</label>
                                            <input
                                                type="text"
                                                value={settings.unboxing_title}
                                                onChange={(e) => handleChange('unboxing_title', e.target.value)}
                                                className={inputClasses}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Descripción</label>
                                        <textarea
                                            value={settings.unboxing_description}
                                            onChange={(e) => handleChange('unboxing_description', e.target.value)}
                                            rows={2}
                                            className={inputClasses}
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <label className={labelClasses}>Inclusiones (Features)</label>
                                        <input value={settings.unboxing_feature_1} onChange={(e) => handleChange('unboxing_feature_1', e.target.value)} className={inputClasses} placeholder="Feature 1" />
                                        <input value={settings.unboxing_feature_2} onChange={(e) => handleChange('unboxing_feature_2', e.target.value)} className={inputClasses} placeholder="Feature 2" />
                                        <input value={settings.unboxing_feature_3} onChange={(e) => handleChange('unboxing_feature_3', e.target.value)} className={inputClasses} placeholder="Feature 3" />
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-6">
                                {renderFramingControls('unboxing')}
                            </div>
                        </div>
                    </div>
                )}

                {/* Bottom CTA Section */}
                {activeTab === 'cta' && (
                    <div className={`${sectionClasses} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
                        <div className="flex items-center gap-2 mb-6">
                            <h2 className="text-xl font-bold text-white uppercase italic tracking-tighter">Llamado a la Acción (Final)</h2>
                        </div>
                        <div className="grid gap-6">
                            <div>
                                <label className={labelClasses}>Frase Final</label>
                                <input
                                    type="text"
                                    value={settings.cta_title}
                                    onChange={(e) => handleChange('cta_title', e.target.value)}
                                    className={inputClasses}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NarrativeSettings;
